import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'startersTab'
	| 'chatsTab'
	| 'sourcesTab'
	| 'memoryTab'
	| 'noChatsTitle'
	| 'noChats'
	| 'untitledChat'
	| 'openChatLabel'
	| 'loadingChatsLabel'
	| 'deleteMemoryLabel'
	| 'deleteMemoryError'
	| 'loadMemoriesError'
	| 'addMemoryButton'
	| 'noStarters'
	| 'noSources'
	| 'noSourcesTitle'
	| 'addSourcesButton'
	| 'tabListLabel'
	| 'aiDisclaimer'
	| 'deleteSourceLabel'
	| 'noMemoriesTitle'
	| 'noMemories'
	| 'submitNewMemory'
	| 'editTitleLabel'
	| 'editDescriptionLabel'
	| 'changeEmojiLabel'
	| 'addDescriptionButton'
	| 'titleInputLabel'
	| 'descriptionInputLabel'
	| 'saveTitleButton'
	| 'cancelTitleButton'
	| 'saveDescriptionButton'
	| 'cancelDescriptionButton'
	| 'titleRequired'
	| 'saveDetailsError'
	| 'addLinksInvalidDataError';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		startersTab: {
			id: 'ai-mate.rovo-spaces.space-landing-page.starters-tab',
			defaultMessage: 'Starters',
			description: 'Tab label for the Starters tab on the Rovo Space landing page.',
		},
		chatsTab: {
			id: 'ai-mate.rovo-spaces.space-landing-page.chats-tab',
			defaultMessage: 'Chats',
			description: 'Tab label for the Chats tab on the Rovo Space landing page.',
		},
		sourcesTab: {
			id: 'ai-mate.rovo-spaces.space-landing-page.sources-tab',
			defaultMessage: 'Sources',
			description: 'Tab label for the Sources tab on the Rovo Space landing page.',
		},
		memoryTab: {
			id: 'ai-mate.rovo-spaces.space-landing-page.memory-tab',
			defaultMessage: 'Memory',
			description: 'Tab label for the Memory tab on the Rovo Space landing page.',
		},
		noChatsTitle: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-chats-title',
			defaultMessage: 'No chats yet',
			description: 'Empty state title shown when a Space has no associated chats.',
		},
		noChats: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-chats',
			defaultMessage: 'Chats in {spaceName} will live here.',
			description:
				'Empty state subtext shown when a Space has no associated chats. {spaceName} is the name of the Space.',
		},
		untitledChat: {
			id: 'ai-mate.rovo-spaces.space-landing-page.untitled-chat',
			defaultMessage: 'Untitled chat',
			description: 'Fallback title shown when a Space chat has no name.',
		},
		openChatLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.open-chat-label',
			defaultMessage: 'Open chat {name}',
			description: 'Accessible label for a chat row in the Chats tab. {name} is the chat title.',
		},
		loadingChatsLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.loading-chats-label',
			defaultMessage: 'Loading chats',
			description: 'Accessible label for the loading spinner in the Chats tab.',
		},
		deleteMemoryLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.delete-memory-label',
			defaultMessage: 'Delete {text}',
			description:
				'Accessible label for the delete button on a memory card. {text} is the memory text content.',
		},
		deleteMemoryError: {
			id: 'ai-mate.rovo-spaces.space-landing-page.delete-memory-error',
			defaultMessage: 'Failed to delete memory. Please try again.',
			description: 'Error message shown when deleting a memory item fails.',
		},
		loadMemoriesError: {
			id: 'rovo-spaces.space-landing-page.loadMemoriesError',
			defaultMessage: 'We’re having trouble loading memories. Try refreshing the page.',
			description: 'Error message shown when memory items fail to load for a Rovo Space.',
		},
		addMemoryButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.add-new-memory-button',
			defaultMessage: 'Add new memory',
			description: 'Label for the button that adds a new memory item to the Rovo Space.',
		},
		noStarters: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-starters',
			defaultMessage: 'No starters yet.',
			description: 'Empty state message shown when a Space has no starter prompts.',
		},
		noSources: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-sources',
			defaultMessage:
				'Upload relevant sources and add useful links to help bring your project into focus. The more context you provide, the better Rovo can understand your work and offer thoughtful, tailored support.',
			description:
				'Empty state subtext shown when a Space has no sources, encouraging users to add some.',
		},
		noSourcesTitle: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-sources-title',
			defaultMessage: 'Give your Rovo space more context',
			description: 'Empty state title shown when a Space has no sources.',
		},
		addSourcesButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.add-sources',
			defaultMessage: 'Add sources',
			description:
				'The text is shown on a button in the Rovo space landing page that opens a dialog or panel allowing the user to add new knowledge sources to the space.',
		},
		tabListLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.tab-list-label',
			defaultMessage: 'Space content',
			description: 'Accessible label for the tab list on the Rovo Space landing page.',
		},
		aiDisclaimer: {
			id: 'ai-mate.rovo-spaces.space-landing-page.ai-disclaimer',
			defaultMessage: 'Uses AI. Verify results.',
			description:
				'Disclaimer text shown below the chat input indicating AI-generated content should be verified.',
		},
		deleteSourceLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.delete-source-label',
			defaultMessage: 'Remove source {name}',
			description: 'Accessible label for the delete button on a source card.',
		},
		noMemoriesTitle: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-memories-title',
			defaultMessage: "Let's build some memories",
			description: 'Empty state heading shown when a Space has no memories yet.',
		},
		noMemories: {
			id: 'ai-mate.rovo-spaces.space-landing-page.no-memories',
			defaultMessage:
				'Add memories to help this Rovo space remember what matters. Preferences, goals, context, and decisions will carry across chats.',
			description:
				'Empty state subtext shown when a Space has no memories, encouraging users to add some.',
		},
		submitNewMemory: {
			id: 'ai-mate.rovo-spaces.space-landing-page.submit-new-memory',
			defaultMessage: 'Create',
			description: 'Label for the Create button inside the new memory text field.',
		},
		editTitleLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.edit-title-label',
			defaultMessage: 'Edit Rovo space title',
			description: 'Accessible label for the icon button that edits the Rovo Space title.',
		},
		editDescriptionLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.edit-description-label',
			defaultMessage: 'Edit Rovo space description',
			description: 'Accessible label for the icon button that edits the Rovo Space description.',
		},
		changeEmojiLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.change-emoji-label',
			defaultMessage: 'Change emoji',
			description:
				'Accessible label for the button that opens an emoji picker to change the Rovo Space emoji.',
		},
		addDescriptionButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.add-description-button',
			defaultMessage: 'Add description',
			description:
				'Button label shown when a Rovo Space has no description and the user can add one inline.',
		},
		titleInputLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.title-input-label',
			defaultMessage: 'Rovo space title',
			description: 'Accessible label for the inline text field that edits the Rovo Space title.',
		},
		descriptionInputLabel: {
			id: 'ai-mate.rovo-spaces.space-landing-page.description-input-label',
			defaultMessage: 'Rovo space description',
			description:
				'Accessible label for the inline text area that edits the Rovo Space description.',
		},
		saveTitleButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.save-title-button',
			defaultMessage: 'Save Rovo space title',
			description:
				'Accessible label for the icon-only button that saves inline Rovo Space title edits.',
		},
		cancelTitleButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.cancel-title-button',
			defaultMessage: 'Cancel editing Rovo space title',
			description:
				'Accessible label for the icon-only button that cancels inline Rovo Space title edits.',
		},
		saveDescriptionButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.save-description-button',
			defaultMessage: 'Save Rovo space description',
			description:
				'Accessible label for the icon-only button that saves inline Rovo Space description edits.',
		},
		cancelDescriptionButton: {
			id: 'ai-mate.rovo-spaces.space-landing-page.cancel-description-button',
			defaultMessage: 'Cancel editing Rovo space description',
			description:
				'Accessible label for the icon-only button that cancels inline Rovo Space description edits.',
		},
		titleRequired: {
			id: 'ai-mate.rovo-spaces.space-landing-page.title-required',
			defaultMessage: 'Enter a Rovo space title.',
			description: 'Validation error shown when the inline Rovo Space title field is empty.',
		},
		saveDetailsError: {
			id: 'ai-mate.rovo-spaces.space-landing-page.save-details-error',
			defaultMessage: "We couldn't save your changes. Try again.",
			description:
				'Error message shown when inline Rovo Space title or description changes cannot be saved.',
		},
		addLinksInvalidDataError: {
			id: 'ai-mate.rovo-spaces.space-landing-page.add-links-invalid-data-error',
			defaultMessage: 'Could not add source — invalid data',
			description:
				'Error message shown when one or more recommended sources cannot be added to a Rovo Space because the recommendation data is invalid.',
		},
	});

export default message;
